import React from 'react';
import { Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing, type, useAppTheme } from '@/theme';
import { ScaledSheet } from '@/theme/responsive';
import { trText } from '@/i18n/plain';
import { useLanguage } from '@/i18n';

interface Props {
  rating: number;
  reviewCount?: number;
  size?: number;
  showValue?: boolean;
  starsOnly?: boolean;
  /** A small card has no room for "(1288)": show "(99+)" instead. */
  shortCount?: boolean;
}

/** Filled/outline star row — used everywhere a rating appears so scores always render
 *  identically (cards, shop details, review screens). */
export function RatingStars({ rating, reviewCount, size = 13, showValue = true, starsOnly = false, shortCount = false }: Props) {
  useLanguage(); // redraw when the language changes (trText is read at call time)
  const { colors } = useAppTheme();
  const rounded = Math.round(rating);
  if (starsOnly) {
    return (
      <View style={styles.starsRow}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Ionicons key={i} name={i <= rounded ? 'star' : 'star-outline'} size={size} color={i <= rounded ? colors.star : colors.starMuted} />
        ))}
      </View>
    );
  }
  // Nobody has reviewed them yet: "★ 0.0 (0)" read like a zero-star score.
  if (reviewCount === 0 || (reviewCount === undefined && rating === 0)) {
    return (
      <View style={styles.row}>
        <Ionicons name="sparkles" size={size} color={colors.accent} />
        <Text style={[type.caption, { color: colors.accent }]}>{trText('New', 'নতুন')}</Text>
      </View>
    );
  }
  return (
    <View style={styles.row}>
      <Ionicons name="star" size={size} color={colors.star} />
      {showValue && <Text style={[type.caption, shortCount && styles.small, { color: colors.textPrimary }]}>{rating.toFixed(1)}</Text>}
      {reviewCount !== undefined && <Text style={[type.caption, shortCount && styles.small, { color: colors.textTertiary }]}>({shortCount && reviewCount > 99 ? '99+' : reviewCount})</Text>}
    </View>
  );
}

const styles = ScaledSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  starsRow: { flexDirection: 'row', gap: 2 },
  small: { fontSize: 11 },
  spacer: { width: spacing.xs },
});
